import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { arApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Wallet, AlertTriangle, TrendingUp, TrendingDown, Send, IndianRupee,
  Loader2, Search, Filter, Eye, MessageSquare, FileWarning, Calendar,
  CalendarClock, CheckCircle2, Clock, Receipt, XCircle, RefreshCw, Settings as SettingsIcon,
  Sparkles, Zap, Phone, BellOff, Edit3, ExternalLink, Trash2
} from 'lucide-react';
import { cn } from '../lib/utils';
import BulkReminderDialog from '../components/BulkReminderDialog';

const formatINR = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)} Cr`;
  if (Math.abs(v) >= 1_00_000) return `₹${(v / 1_00_000).toFixed(2)} L`;
  if (Math.abs(v) >= 1_000) return `₹${(v / 1_000).toFixed(1)}k`;
  return `₹${v.toFixed(0)}`;
};

const formatINRFull = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const BUCKET_LABELS = {
  current: 'Current',
  '0_30': '0-30 days',
  '31_60': '31-60 days',
  '61_90': '61-90 days',
  '91_120': '91-120 days',
  '120_plus': '120+ days',
};

const BUCKET_COLOR = {
  current: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
  '0_30': 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  '31_60': 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300',
  '61_90': 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300',
  '91_120': 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
  '120_plus': 'bg-red-200 text-red-900 dark:bg-red-700/30 dark:text-red-100',
};

const STATUS_COLOR = {
  current: 'text-blue-600',
  partial: 'text-amber-600',
  overdue: 'text-red-600',
  paid: 'text-emerald-600',
  disputed: 'text-purple-600',
};

// ─── AR Hub ─────────────────────────────────────────────────
const ARHub = ({ onOpenInvoice }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    arApi.dashboard()
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load AR dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>;
  }
  if (!data) return null;

  const kpis = [
    { label: 'Total Outstanding', value: formatINR(data.total_outstanding), full: formatINRFull(data.total_outstanding), icon: IndianRupee, color: 'from-amber-500 to-orange-600' },
    { label: 'Overdue', value: formatINR(data.total_overdue), full: `${data.overdue_pct}% overdue`, icon: AlertTriangle, color: 'from-red-500 to-rose-600' },
    { label: 'DSO', value: `${data.dso}d`, full: 'Days Sales Outstanding', icon: CalendarClock, color: 'from-blue-500 to-cyan-600' },
    { label: 'Cash expected (30d)', value: formatINR(data.cash_next_30_days), full: 'Best-case scenario', icon: TrendingUp, color: 'from-emerald-500 to-green-600' },
    { label: 'Open invoices', value: data.open_invoice_count, full: `${(data.open_invoice_count || 0)} unsettled`, icon: Receipt, color: 'from-indigo-500 to-violet-600' },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        {kpis.map(k => (
          <Card key={k.label} className="overflow-hidden" data-testid={`ar-kpi-${k.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{k.label}</span>
                <div className={cn("h-7 w-7 rounded-lg bg-gradient-to-br flex items-center justify-center", k.color)}>
                  <k.icon className="h-3.5 w-3.5 text-white" />
                </div>
              </div>
              <p className="font-heading text-2xl font-bold tracking-tight">{k.value}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{k.full}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Aging buckets */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Calendar className="h-4 w-4 text-amber-500" /> Aging Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            {Object.entries(data.buckets || {}).map(([key, v]) => (
              <div key={key} className={cn("rounded-lg p-3", BUCKET_COLOR[key])} data-testid={`ar-bucket-${key}`}>
                <p className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{BUCKET_LABELS[key] || key}</p>
                <p className="text-lg font-bold mt-1 tabular-nums">{formatINR(v.amount)}</p>
                <p className="text-[10px] mt-0.5 opacity-80">{v.count} invoice{v.count === 1 ? '' : 's'}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Debtors */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-rose-500" /> Top 5 Debtors
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {(data.top_debtors || []).length === 0 ? (
            <p className="text-sm text-muted-foreground p-6 text-center">No outstanding debtors. 🎉</p>
          ) : (
            <div className="divide-y divide-border">
              {(data.top_debtors || []).map((d, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40">
                  <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-rose-400 to-orange-500 text-white flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{d.customer_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {d.invoice_count} invoice{d.invoice_count === 1 ? '' : 's'}
                      {d.max_days_overdue > 0 && (
                        <span className="text-rose-600 font-medium"> · {d.max_days_overdue}d overdue max</span>
                      )}
                      {d.branch && <span> · {d.branch}</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold tabular-nums">{formatINR(d.outstanding)}</p>
                    {d.overdue > 0 && <p className="text-[10px] text-rose-600 tabular-nums">{formatINR(d.overdue)} overdue</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Open Invoices ──────────────────────────────────────────
const OpenInvoices = ({ onAction }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ invoices: [], total: 0 });
  const [status, setStatus] = useState('all');
  const [bucket, setBucket] = useState('all');
  const [search, setSearch] = useState('');

  const fetchInvoices = useCallback(() => {
    setLoading(true);
    const params = {};
    if (status !== 'all') params.status = status;
    if (bucket !== 'all') params.bucket = bucket;
    if (search) params.search = search;
    arApi.invoices(params)
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load invoices'))
      .finally(() => setLoading(false));
  }, [status, bucket, search]);

  useEffect(() => {
    const t = setTimeout(fetchInvoices, 300);
    return () => clearTimeout(t);
  }, [fetchInvoices]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[140px] basis-full sm:basis-auto">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search CO / customer / invoice #"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="ar-invoice-search"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="flex-1 min-w-0 sm:w-[160px] sm:flex-none" data-testid="ar-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="current">Current</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="disputed">Disputed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={bucket} onValueChange={setBucket}>
          <SelectTrigger className="flex-1 min-w-0 sm:w-[160px] sm:flex-none" data-testid="ar-bucket-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ages</SelectItem>
            <SelectItem value="current">Current</SelectItem>
            <SelectItem value="0_30">0-30 days</SelectItem>
            <SelectItem value="31_60">31-60 days</SelectItem>
            <SelectItem value="61_90">61-90 days</SelectItem>
            <SelectItem value="91_120">91-120 days</SelectItem>
            <SelectItem value="120_plus">120+ days</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchInvoices} disabled={loading} data-testid="ar-refresh">
          <RefreshCw className={cn("h-4 w-4 md:mr-1", loading && "animate-spin")} /> <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : data.invoices.length === 0 ? (
            <p className="p-12 text-sm text-muted-foreground text-center">No invoices match these filters.</p>
          ) : (
            <div className="divide-y divide-border" data-testid="ar-invoices-list">
              <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 bg-muted/40 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                <div className="col-span-2">Invoice / CO</div>
                <div className="col-span-3">Customer</div>
                <div className="col-span-1 text-right">Total</div>
                <div className="col-span-1 text-right">Paid</div>
                <div className="col-span-1 text-right">Balance</div>
                <div className="col-span-1">Due</div>
                <div className="col-span-1">Overdue</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-1 text-right">Actions</div>
              </div>
              {data.invoices.map(inv => (
                <div key={`${inv.order_id}-${inv.dispatch_id}`} className="hover:bg-muted/30" data-testid={`ar-invoice-row-${inv.invoice_number}`}>
                  {/* Desktop: 12-col grid */}
                  <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 items-center text-sm">
                    <div className="col-span-2">
                      <p className="font-semibold">{inv.invoice_number || '—'}</p>
                      <p className="text-[11px] text-muted-foreground">{inv.co_number}</p>
                    </div>
                    <div className="col-span-3 min-w-0">
                      <p className="font-medium truncate">{inv.customer_name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {inv.branch && `${inv.branch} · `}{inv.salesperson || '—'}
                      </p>
                    </div>
                    <div className="col-span-1 text-right tabular-nums">{formatINR(inv.total_amount)}</div>
                    <div className="col-span-1 text-right tabular-nums text-emerald-600">{formatINR(inv.paid_amount)}</div>
                    <div className="col-span-1 text-right tabular-nums font-semibold">{formatINR(inv.balance)}</div>
                    <div className="col-span-1 text-[11px] text-muted-foreground">{inv.due_date || '—'}</div>
                    <div className="col-span-1">
                      {inv.days_overdue > 0
                        ? <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/20 dark:text-rose-300">{inv.days_overdue}d</Badge>
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </div>
                    <div className="col-span-1">
                      <span className={cn("text-xs font-semibold capitalize", STATUS_COLOR[inv.status] || 'text-slate-600')}>
                        {inv.status}
                      </span>
                    </div>
                    <div className="col-span-1 flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-7 px-1.5" title="Send Reminder"
                              onClick={() => onAction('reminder', inv)} data-testid={`ar-reminder-${inv.invoice_number}`}>
                        <MessageSquare className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-1.5" title="More"
                              onClick={() => onAction('detail', inv)} data-testid={`ar-detail-${inv.invoice_number}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Mobile: card layout */}
                  <div className="md:hidden px-3 py-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm truncate">{inv.customer_name}</p>
                        <p className="text-[11px] text-muted-foreground truncate">
                          {inv.invoice_number || '—'} · {inv.co_number}
                          {inv.branch && ` · ${inv.branch}`}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold tabular-nums">{formatINR(inv.balance)}</p>
                        <p className="text-[10px] text-muted-foreground">balance</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap text-[11px]">
                      <span className={cn("font-semibold capitalize", STATUS_COLOR[inv.status] || 'text-slate-600')}>
                        {inv.status}
                      </span>
                      {inv.days_overdue > 0 && (
                        <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/20 dark:text-rose-300 text-[10px]">{inv.days_overdue}d overdue</Badge>
                      )}
                      <span className="text-muted-foreground">Due {inv.due_date || '—'}</span>
                      <span className="text-muted-foreground tabular-nums">·  Total {formatINR(inv.total_amount)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 pt-1">
                      <Button variant="outline" size="sm" className="h-8 flex-1 text-xs" onClick={() => onAction('reminder', inv)} data-testid={`ar-reminder-mobile-${inv.invoice_number}`}>
                        <MessageSquare className="h-3.5 w-3.5 mr-1" /> Remind
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 px-2.5 text-xs" onClick={() => onAction('detail', inv)} data-testid={`ar-detail-mobile-${inv.invoice_number}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Aging Report ───────────────────────────────────────────
const AgingReport = () => {
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState('customer');
  const [data, setData] = useState({ groups: [] });
  const [bulkReminderCustomer, setBulkReminderCustomer] = useState(null);

  useEffect(() => {
    setLoading(true);
    arApi.agingReport({ group_by: groupBy })
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load aging report'))
      .finally(() => setLoading(false));
  }, [groupBy]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-xs">Group by:</Label>
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="customer">Customer</SelectItem>
            <SelectItem value="salesperson">Salesperson</SelectItem>
            <SelectItem value="branch">Branch</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-4 space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-3 py-2.5">{groupBy === 'customer' ? 'Customer' : groupBy === 'branch' ? 'Branch' : 'Salesperson'}</th>
                  <th className="text-right px-2 py-2.5">Current</th>
                  <th className="text-right px-2 py-2.5">0-30</th>
                  <th className="text-right px-2 py-2.5">31-60</th>
                  <th className="text-right px-2 py-2.5">61-90</th>
                  <th className="text-right px-2 py-2.5">91-120</th>
                  <th className="text-right px-2 py-2.5">120+</th>
                  <th className="text-right px-3 py-2.5">Total</th>
                  {groupBy === 'customer' && <th className="text-right px-3 py-2.5">Action</th>}
                </tr>
              </thead>
              <tbody>
                {(data.groups || []).map((g, i) => (
                  <tr key={i} className="border-t hover:bg-muted/30" data-testid={`ar-aging-row-${g.label}`}>
                    <td className="px-3 py-2.5 font-medium">{g.label}</td>
                    <td className="text-right px-2 py-2.5 tabular-nums">{formatINR(g.buckets.current)}</td>
                    <td className="text-right px-2 py-2.5 tabular-nums">{formatINR(g.buckets['0_30'])}</td>
                    <td className="text-right px-2 py-2.5 tabular-nums">{formatINR(g.buckets['31_60'])}</td>
                    <td className="text-right px-2 py-2.5 tabular-nums">{formatINR(g.buckets['61_90'])}</td>
                    <td className="text-right px-2 py-2.5 tabular-nums">{formatINR(g.buckets['91_120'])}</td>
                    <td className="text-right px-2 py-2.5 tabular-nums text-rose-600 font-semibold">{formatINR(g.buckets['120_plus'])}</td>
                    <td className="text-right px-3 py-2.5 tabular-nums font-bold">{formatINR(g.total)}</td>
                    {groupBy === 'customer' && (
                      <td className="text-right px-3 py-2.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          title="Send statement of all pending invoices via WhatsApp"
                          disabled={!g.customer_id || (g.total || 0) <= 0}
                          onClick={() => setBulkReminderCustomer({ id: g.customer_id, company_name: g.label })}
                          data-testid={`ar-aging-send-statement-${g.label}`}
                        >
                          <MessageSquare className="h-3.5 w-3.5 mr-1" /> Statement
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
                {(data.groups || []).length === 0 && (
                  <tr><td colSpan={groupBy === 'customer' ? 9 : 8} className="text-center py-8 text-muted-foreground">No data</td></tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <BulkReminderDialog
        open={!!bulkReminderCustomer}
        customer={bulkReminderCustomer}
        onClose={() => setBulkReminderCustomer(null)}
      />
    </div>
  );
};

// ─── Cash Forecast ──────────────────────────────────────────
const CashForecast = () => {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(60);
  const [data, setData] = useState(null);

  useEffect(() => {
    setLoading(true);
    arApi.cashForecast(days)
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load forecast'))
      .finally(() => setLoading(false));
  }, [days]);

  const totals = useMemo(() => {
    if (!data) return null;
    const sum = arr => arr.reduce((acc, x) => acc + x.amount, 0);
    return {
      best: sum(data.scenarios.best || []),
      likely: sum(data.scenarios.likely || []),
      conservative: sum(data.scenarios.conservative || []),
    };
  }, [data]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-xs">Horizon:</Label>
        <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="60">60 days</SelectItem>
            <SelectItem value="90">90 days</SelectItem>
            <SelectItem value="180">180 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <Skeleton className="h-32 w-full" /> : totals && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Best case</p>
            <p className="text-2xl font-bold mt-1 text-emerald-600">{formatINR(totals.best)}</p>
            <p className="text-[11px] text-muted-foreground">If everyone pays on due date</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Most likely</p>
            <p className="text-2xl font-bold mt-1 text-amber-600">{formatINR(totals.likely)}</p>
            <p className="text-[11px] text-muted-foreground">Based on customer behavior</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Conservative</p>
            <p className="text-2xl font-bold mt-1 text-rose-600">{formatINR(totals.conservative)}</p>
            <p className="text-[11px] text-muted-foreground">90th-percentile lateness</p>
          </CardContent></Card>
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Most-likely cash inflow timeline</CardTitle></CardHeader>
        <CardContent>
          {!data || (data.scenarios.likely || []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No forecast data available</p>
          ) : (
            <div className="space-y-1.5 max-h-[480px] overflow-y-auto">
              {(data.scenarios.likely || []).map(pt => (
                <div key={pt.date} className="flex items-center gap-2 md:gap-3">
                  <span className="text-[11px] md:text-xs text-muted-foreground tabular-nums w-16 md:w-24 flex-shrink-0">{pt.date}</span>
                  <div className="flex-1 h-7 bg-muted/40 rounded-md overflow-hidden relative min-w-0">
                    <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-400 to-orange-500"
                         style={{ width: `${Math.min(100, (pt.amount / Math.max(...data.scenarios.likely.map(x => x.amount), 1)) * 100)}%` }} />
                  </div>
                  <span className="text-xs md:text-sm font-semibold tabular-nums w-16 md:w-24 text-right flex-shrink-0">{formatINR(pt.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Disputes & PTP ─────────────────────────────────────────
const DisputesAndPTP = () => {
  const [tab, setTab] = useState('ptp');
  const [ptps, setPtps] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ptpStatus, setPtpStatus] = useState('active');
  const [disputeStatus, setDisputeStatus] = useState('open');

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      arApi.listPTP(ptpStatus).then(r => setPtps(r.data.promises || [])),
      arApi.listDisputes(disputeStatus).then(r => setDisputes(r.data.disputes || [])),
    ]).finally(() => setLoading(false));
  }, [ptpStatus, disputeStatus]);

  useEffect(reload, [reload]);

  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList>
        <TabsTrigger value="ptp" data-testid="ar-ptp-tab">Promises-to-Pay ({ptps.length})</TabsTrigger>
        <TabsTrigger value="disputes" data-testid="ar-disputes-tab">Disputes ({disputes.length})</TabsTrigger>
      </TabsList>
      <TabsContent value="ptp" className="space-y-3 mt-4">
        <Select value={ptpStatus} onValueChange={setPtpStatus}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="honored">Honored</SelectItem>
            <SelectItem value="broken">Broken</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Card><CardContent className="p-0">
          {loading ? <Skeleton className="h-32 w-full" /> :
           ptps.length === 0 ? <p className="p-8 text-sm text-muted-foreground text-center">No promises in this state.</p> :
           <div className="divide-y">
             {ptps.map(p => (
               <div key={p.id} className="px-3 md:px-4 py-3 hover:bg-muted/30">
                 {/* Desktop */}
                 <div className="hidden md:grid grid-cols-12 gap-2 items-center text-sm">
                   <div className="col-span-4">
                     <p className="font-semibold">{p.customer_name}</p>
                     <p className="text-[11px] text-muted-foreground">{p.channel} · by {p.created_by}</p>
                   </div>
                   <div className="col-span-2 text-sm">{p.promised_date}</div>
                   <div className="col-span-2 text-right tabular-nums font-semibold">{formatINR(p.promised_amount)}</div>
                   <div className="col-span-2 text-xs text-muted-foreground truncate">{p.notes || '—'}</div>
                   <div className="col-span-2 flex items-center justify-end gap-1">
                     <Button size="sm" variant="ghost" className="h-7 text-emerald-600" title="Mark honored"
                             onClick={() => arApi.updatePTP(p.id, 'honored').then(() => { toast.success('Marked honored'); reload(); })}>
                       <CheckCircle2 className="h-3.5 w-3.5" />
                     </Button>
                     <Button size="sm" variant="ghost" className="h-7 text-rose-600" title="Mark broken"
                             onClick={() => arApi.updatePTP(p.id, 'broken').then(() => { toast.success('Marked broken'); reload(); })}>
                       <XCircle className="h-3.5 w-3.5" />
                     </Button>
                   </div>
                 </div>
                 {/* Mobile */}
                 <div className="md:hidden space-y-1.5 text-sm">
                   <div className="flex items-start justify-between gap-2">
                     <div className="min-w-0">
                       <p className="font-semibold truncate">{p.customer_name}</p>
                       <p className="text-[11px] text-muted-foreground">{p.channel} · by {p.created_by}</p>
                     </div>
                     <p className="font-semibold tabular-nums flex-shrink-0">{formatINR(p.promised_amount)}</p>
                   </div>
                   <p className="text-xs"><span className="text-muted-foreground">Promised:</span> {p.promised_date}</p>
                   {p.notes && <p className="text-[11px] text-muted-foreground line-clamp-2">{p.notes}</p>}
                   <div className="flex items-center gap-1.5 pt-1">
                     <Button size="sm" variant="outline" className="h-8 flex-1 text-xs text-emerald-700"
                             onClick={() => arApi.updatePTP(p.id, 'honored').then(() => { toast.success('Marked honored'); reload(); })}>
                       <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Honored
                     </Button>
                     <Button size="sm" variant="outline" className="h-8 flex-1 text-xs text-rose-600"
                             onClick={() => arApi.updatePTP(p.id, 'broken').then(() => { toast.success('Marked broken'); reload(); })}>
                       <XCircle className="h-3.5 w-3.5 mr-1" /> Broken
                     </Button>
                   </div>
                 </div>
               </div>
             ))}
           </div>}
        </CardContent></Card>
      </TabsContent>
      <TabsContent value="disputes" className="space-y-3 mt-4">
        <Select value={disputeStatus} onValueChange={setDisputeStatus}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Card><CardContent className="p-0">
          {loading ? <Skeleton className="h-32 w-full" /> :
           disputes.length === 0 ? <p className="p-8 text-sm text-muted-foreground text-center">No disputes in this state.</p> :
           <div className="divide-y">
             {disputes.map(d => (
               <div key={d.id} className="px-3 md:px-4 py-3 hover:bg-muted/30">
                 {/* Desktop */}
                 <div className="hidden md:grid grid-cols-12 gap-2 items-center text-sm">
                   <div className="col-span-4">
                     <p className="font-semibold">{d.customer_name}</p>
                     <p className="text-[11px] text-muted-foreground">Inv #{d.invoice_number}</p>
                   </div>
                   <div className="col-span-2"><Badge variant="outline" className="text-[10px] capitalize">{d.category}</Badge></div>
                   <div className="col-span-4 text-xs text-muted-foreground truncate">{d.reason}</div>
                   <div className="col-span-2 flex items-center justify-end gap-2">
                     <Badge variant="outline" className={cn("text-[10px] capitalize", d.status === 'open' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700')}>{d.status}</Badge>
                   </div>
                 </div>
                 {/* Mobile */}
                 <div className="md:hidden space-y-1.5 text-sm">
                   <div className="flex items-start justify-between gap-2">
                     <div className="min-w-0">
                       <p className="font-semibold truncate">{d.customer_name}</p>
                       <p className="text-[11px] text-muted-foreground">Inv #{d.invoice_number}</p>
                     </div>
                     <Badge variant="outline" className={cn("text-[10px] capitalize flex-shrink-0", d.status === 'open' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700')}>{d.status}</Badge>
                   </div>
                   <div className="flex items-center gap-2 flex-wrap">
                     <Badge variant="outline" className="text-[10px] capitalize">{d.category}</Badge>
                     <p className="text-[11px] text-muted-foreground line-clamp-2 flex-1">{d.reason}</p>
                   </div>
                 </div>
               </div>
             ))}
           </div>}
        </CardContent></Card>
      </TabsContent>
    </Tabs>
  );
};

// ─── Settings ───────────────────────────────────────────────
const ARSettingsPanel = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [s, setS] = useState(null);
  // Orphan-payment cleanup (super_admin only)
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    arApi.getSettings().then(r => setS(r.data)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await arApi.updateSettings(s);
      toast.success('Settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };
  const runNow = async () => {
    setRunning(true);
    try {
      const res = await arApi.runAutoReminders();
      const d = res.data || {};
      toast.success(`Sent ${d.sent || 0} · skipped ${d.skipped || 0}`);
    } catch { toast.error('Failed to run reminders'); }
    finally { setRunning(false); }
  };

  const openCleanup = async () => {
    setCleanupOpen(true);
    setCleanupLoading(true);
    try {
      const res = await arApi.getOrphanPayments();
      setCleanupPreview(res.data || { count: 0, payments: [], total_amount: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load payment list');
      setCleanupOpen(false);
    } finally {
      setCleanupLoading(false);
    }
  };

  const doPurge = async () => {
    setPurging(true);
    try {
      const res = await arApi.purgeOrphanPayments();
      const removed = res?.data?.removed || 0;
      toast.success(`Removed ${removed} legacy payment${removed === 1 ? '' : 's'}. Ledger + Receivables auto-refresh.`);
      setCleanupOpen(false);
      setCleanupPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to purge payments');
    } finally {
      setPurging(false);
    }
  };

  if (loading || !s) return <Skeleton className="h-64 w-full" />;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><SettingsIcon className="h-4 w-4" /> AR Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Default payment terms (days)</Label>
            <Input type="number" value={s.default_payment_terms_days}
                   onChange={e => setS({ ...s, default_payment_terms_days: Number(e.target.value) })}
                   data-testid="ar-settings-terms" />
            <p className="text-[11px] text-muted-foreground mt-1">Per-customer overrides take precedence.</p>
          </div>
          <div>
            <Label>Reminder cadence (days from due date, comma-separated)</Label>
            <Input value={(s.reminder_cadence_days || []).join(', ')}
                   onChange={e => setS({ ...s, reminder_cadence_days: e.target.value.split(',').map(x => parseInt(x.trim())).filter(x => !isNaN(x)) })}
                   data-testid="ar-settings-cadence" />
            <p className="text-[11px] text-muted-foreground mt-1">Negative = before due. e.g. -3, 0, 3, 7, 15, 30, 60</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant={s.auto_reminders_enabled ? 'default' : 'outline'} size="sm"
                  onClick={() => setS({ ...s, auto_reminders_enabled: !s.auto_reminders_enabled })}
                  data-testid="ar-settings-auto-toggle">
            {s.auto_reminders_enabled ? '✓ Auto-reminders ON' : 'Auto-reminders OFF'}
          </Button>
          <Button variant={s.block_orders_over_credit_limit ? 'default' : 'outline'} size="sm"
                  onClick={() => setS({ ...s, block_orders_over_credit_limit: !s.block_orders_over_credit_limit })}>
            {s.block_orders_over_credit_limit ? '✓ Block over credit limit' : 'Warn only over limit'}
          </Button>
          <Button variant={s.skip_weekends ? 'default' : 'outline'} size="sm"
                  onClick={() => setS({ ...s, skip_weekends: !s.skip_weekends })}>
            {s.skip_weekends ? '✓ Skip weekends' : 'Include weekends'}
          </Button>
        </div>
        <div className="flex gap-2 pt-2 border-t">
          <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="ar-settings-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save Settings
          </Button>
          <Button variant="outline" onClick={runNow} disabled={running} data-testid="ar-run-reminders">
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Run auto-reminders now
          </Button>
        </div>

        {isSuperAdmin && (
          <div className="mt-4 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50/60 dark:bg-red-500/10 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
              <p className="text-sm font-semibold text-red-800 dark:text-red-300">Legacy AR-module payments</p>
            </div>
            <p className="text-xs text-red-700/80 dark:text-red-300/70">
              Old customer payments recorded via the removed AR-module UI. Deleting them
              frees up the invoice balance and cleans up the ledger. This is irreversible
              (an audit movement is written for each removed payment).
            </p>
            <Button
              size="sm" variant="outline"
              className="border-red-400 text-red-700 hover:bg-red-100 dark:hover:bg-red-500/20"
              onClick={openCleanup} data-testid="ar-cleanup-open-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Review & purge legacy payments
            </Button>
          </div>
        )}
      </CardContent>

      <Dialog open={cleanupOpen} onOpenChange={(o) => !o && !purging && setCleanupOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ar-cleanup-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <Trash2 className="h-4 w-4" /> Purge legacy AR-module payments
            </DialogTitle>
            <DialogDescription>
              These payments were recorded via the (now-removed) AR Receivables Module UI. Review before deleting.
            </DialogDescription>
          </DialogHeader>

          {cleanupLoading ? (
            <div className="py-8 flex items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : !cleanupPreview || cleanupPreview.count === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="ar-cleanup-empty">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
              No legacy AR-module payments found. Nothing to clean up.
            </div>
          ) : (
            <>
              <div className="rounded-md bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 p-2.5 text-xs text-red-800 dark:text-red-200">
                Will remove <b>{cleanupPreview.count}</b> payment{cleanupPreview.count === 1 ? '' : 's'} totalling
                {' '}<b>₹{Number(cleanupPreview.total_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</b>.
                Invoice balances + ledger will auto-recompute after removal.
              </div>
              <div className="rounded-md border overflow-hidden">
                <div className="max-h-[45vh] overflow-y-auto divide-y">
                  {cleanupPreview.payments.map(p => (
                    <div key={p.payment_id} className="px-3 py-2 text-xs flex items-center justify-between gap-2" data-testid={`ar-cleanup-row-${p.payment_id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold truncate">
                          {p.customer_name || '—'} · <span className="text-muted-foreground">Inv {p.invoice_number}</span>
                        </p>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {p.date} · {p.payment_mode || 'bank'}{p.reference_number ? ` · ${p.reference_number}` : ''} · by {p.recorded_by || '—'}
                        </p>
                      </div>
                      <span className="tabular-nums font-mono text-sm font-semibold text-red-700 dark:text-red-400 flex-shrink-0">
                        ₹{Number(p.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCleanupOpen(false)} disabled={purging}>Cancel</Button>
            <Button
              onClick={doPurge}
              disabled={purging || cleanupLoading || !cleanupPreview?.count}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="ar-cleanup-purge-btn"
            >
              {purging ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
              {purging ? 'Purging…' : `Delete ${cleanupPreview?.count || 0} payment${cleanupPreview?.count === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ─── Action Dialogs ─────────────────────────────────────────
const PaymentDialog = ({ open, invoice, onClose, onSaved }) => {
  const [form, setForm] = useState({ amount: '', payment_mode: 'rtgs', reference_number: '', date: new Date().toISOString().slice(0,10), notes: '', tds_amount: 0 });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && invoice) setForm(f => ({ ...f, amount: invoice.balance }));
  }, [open, invoice]);
  if (!invoice) return null;
  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Amount required'); return; }
    setSaving(true);
    try {
      await arApi.recordPayment(invoice.order_id, invoice.dispatch_id, { ...form, amount: Number(form.amount), tds_amount: Number(form.tds_amount || 0) });
      toast.success('Payment recorded');
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="ar-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>{invoice.customer_name} · Invoice {invoice.invoice_number} · {formatINRFull(invoice.balance)} pending</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount *</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="ar-payment-amount" /></div>
            <div><Label>TDS</Label><Input type="number" step="0.01" value={form.tds_amount} onChange={e => setForm({ ...form, tds_amount: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Mode</Label>
              <Select value={form.payment_mode} onValueChange={v => setForm({ ...form, payment_mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rtgs">RTGS / NEFT</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="metal_exchange">Metal Exchange</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
          </div>
          <div><Label>Reference #</Label><Input value={form.reference_number} onChange={e => setForm({ ...form, reference_number: e.target.value })} /></div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ar-payment-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReminderDialog = ({ open, invoice, onClose, onSaved }) => {
  const [message, setMessage] = useState('');
  const [phones, setPhones] = useState([]);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (open && invoice) {
      const seen = new Set();
      const list = [];
      if (invoice.customer_phone) { seen.add(invoice.customer_phone); list.push({ phone: invoice.customer_phone, label: 'Primary' }); }
      (invoice.notification_phones || []).forEach(p => {
        if (p?.phone && !seen.has(p.phone)) { seen.add(p.phone); list.push({ phone: p.phone, label: p.name || 'Contact' }); }
      });
      setPhones(list.map(l => ({ ...l, checked: true })));
      // If invoice carries an AI-generated draft, pre-fill it
      setMessage(invoice._ai_draft || '');
    }
  }, [open, invoice]);
  if (!invoice) return null;
  const submit = async () => {
    const selected = phones.filter(p => p.checked).map(p => p.phone);
    if (selected.length === 0) { toast.error('Pick at least one recipient'); return; }
    setSending(true);
    try {
      const res = await arApi.sendReminder(invoice.order_id, invoice.dispatch_id, { phones: selected, message: message.trim() || undefined, reminder_type: 'manual' });
      toast.success(res.data?.message || 'Sent');
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="ar-reminder-dialog">
        <DialogHeader>
          <DialogTitle>Send Payment Reminder</DialogTitle>
          <DialogDescription>{invoice.customer_name} · Invoice {invoice.invoice_number} · {formatINRFull(invoice.balance)} pending</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Recipients</Label>
            {phones.length === 0 ? (
              <p className="text-xs text-rose-600">No phone numbers on this order. Add notification contacts in Edit Order.</p>
            ) : (
              <div className="border rounded-md divide-y mt-1 max-h-40 overflow-y-auto">
                {phones.map((p, i) => (
                  <label key={p.phone} className="flex items-center gap-2 px-2.5 py-2 cursor-pointer hover:bg-muted/40">
                    <input type="checkbox" checked={p.checked}
                           onChange={e => setPhones(prev => prev.map((x, j) => j === i ? { ...x, checked: e.target.checked } : x))}
                           data-testid={`ar-reminder-phone-${p.phone}`} />
                    <div className="flex-1 text-sm">
                      <p className="font-medium">{p.label}</p>
                      <p className="text-[11px] text-muted-foreground">{p.phone}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label>Custom message <span className="text-muted-foreground text-[10px]">(optional — leave blank for default)</span></Label>
            <Textarea rows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder="e.g. Dear Sir, kindly arrange payment by end of this week..." data-testid="ar-reminder-message" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={sending || phones.length === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ar-reminder-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DetailDialog = ({ open, invoice, onClose, onSaved }) => {
  const [reminders, setReminders] = useState([]);
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [showPTPDialog, setShowPTPDialog] = useState(false);

  useEffect(() => {
    if (open && invoice) {
      arApi.invoiceReminders(invoice.order_id, invoice.dispatch_id).then(r => setReminders(r.data.reminders || []));
    }
  }, [open, invoice]);

  if (!invoice) return null;
  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ar-detail-dialog">
          <DialogHeader>
            <DialogTitle>{invoice.invoice_number} — {invoice.customer_name}</DialogTitle>
            <DialogDescription>CO {invoice.co_number} · {invoice.branch || '—'} · {invoice.salesperson || '—'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="p-2.5 rounded-lg bg-muted/40"><p className="text-[10px] uppercase text-muted-foreground">Total</p><p className="font-bold">{formatINR(invoice.total_amount)}</p></div>
              <div className="p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10"><p className="text-[10px] uppercase text-muted-foreground">Paid</p><p className="font-bold text-emerald-700 dark:text-emerald-400">{formatINR(invoice.paid_amount)}</p></div>
              <div className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/10"><p className="text-[10px] uppercase text-muted-foreground">Balance</p><p className="font-bold text-amber-700 dark:text-amber-400">{formatINR(invoice.balance)}</p></div>
              <div className="p-2.5 rounded-lg bg-rose-50 dark:bg-rose-500/10"><p className="text-[10px] uppercase text-muted-foreground">Overdue</p><p className="font-bold text-rose-700 dark:text-rose-400">{invoice.days_overdue}d</p></div>
            </div>

            {(invoice.payments || []).length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">Payment history</p>
                <div className="border rounded-md divide-y">
                  {invoice.payments.map(p => (
                    <div key={p.id} className="px-3 py-2 text-xs flex items-center gap-2">
                      <span className="font-semibold tabular-nums">{formatINR(p.amount)}</span>
                      <Badge variant="outline" className="text-[10px]">{p.payment_mode}</Badge>
                      <span className="text-muted-foreground">{p.date}</span>
                      {p.reference_number && <span className="text-muted-foreground">· {p.reference_number}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {reminders.length > 0 && (
              <div>
                <p className="text-sm font-semibold mb-2">Reminders sent ({reminders.length})</p>
                <div className="border rounded-md divide-y max-h-40 overflow-y-auto">
                  {reminders.map(r => (
                    <div key={r.id} className="px-3 py-2 text-xs">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{r.reminder_type}</Badge>
                        <span className="text-muted-foreground">{(r.sent_at || '').slice(0, 16).replace('T', ' ')}</span>
                        <span className="text-emerald-600">→ {r.sent_to.length}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowDisputeDialog(true)} data-testid="ar-mark-dispute">
              <FileWarning className="h-4 w-4 mr-1" /> Mark Disputed
            </Button>
            <Button variant="outline" onClick={() => setShowPTPDialog(true)} data-testid="ar-log-ptp">
              <CalendarClock className="h-4 w-4 mr-1" /> Log Promise
            </Button>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DisputeDialog open={showDisputeDialog} invoice={invoice} onClose={() => setShowDisputeDialog(false)} onSaved={() => { onSaved && onSaved(); onClose(); }} />
      <PTPDialog open={showPTPDialog} invoice={invoice} onClose={() => setShowPTPDialog(false)} onSaved={() => { onSaved && onSaved(); }} />
    </>
  );
};

const DisputeDialog = ({ open, invoice, onClose, onSaved }) => {
  const [category, setCategory] = useState('quality');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  if (!invoice) return null;
  const submit = async () => {
    if (!reason.trim()) { toast.error('Reason required'); return; }
    setSaving(true);
    try {
      await arApi.createDispute(invoice.order_id, invoice.dispatch_id, { category, reason: reason.trim() });
      toast.success('Dispute logged');
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Log Dispute</DialogTitle><DialogDescription>Auto-reminders pause for disputed invoices.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="quality">Quality</SelectItem>
                <SelectItem value="quantity">Quantity</SelectItem>
                <SelectItem value="pricing">Pricing</SelectItem>
                <SelectItem value="documentation">Documentation</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Reason *</Label><Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Log Dispute
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PTPDialog = ({ open, invoice, onClose, onSaved }) => {
  const [form, setForm] = useState({ promised_date: '', promised_amount: '', channel: 'whatsapp', notes: '' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open && invoice) setForm({ promised_date: '', promised_amount: invoice.balance, channel: 'whatsapp', notes: '' }); }, [open, invoice]);
  if (!invoice) return null;
  const submit = async () => {
    if (!form.promised_date) { toast.error('Date required'); return; }
    setSaving(true);
    try {
      await arApi.createPTP(invoice.order_id, invoice.dispatch_id, { ...form, promised_amount: Number(form.promised_amount) });
      toast.success('Promise logged');
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Promise-to-Pay</DialogTitle><DialogDescription>Auto-reminders pause until promise date.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date *</Label><Input type="date" value={form.promised_date} onChange={e => setForm({ ...form, promised_date: e.target.value })} /></div>
            <div><Label>Amount</Label><Input type="number" step="0.01" value={form.promised_amount} onChange={e => setForm({ ...form, promised_amount: e.target.value })} /></div>
          </div>
          <div><Label>Channel</Label>
            <Select value={form.channel} onValueChange={v => setForm({ ...form, channel: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="call">Call</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="in_person">In person</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Log Promise
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── AI Daily Worklist (Phase 3) ─────────────────────────────
const PRIORITY_META = {
  urgent:  { label: 'URGENT',  color: 'bg-red-500',     text: 'text-red-700 dark:text-red-300',     bg: 'bg-red-50 dark:bg-red-500/10',     ring: 'ring-red-300', dot: 'bg-red-500',    emoji: '🔴' },
  high:    { label: 'HIGH',    color: 'bg-orange-500',  text: 'text-orange-700 dark:text-orange-300', bg: 'bg-orange-50 dark:bg-orange-500/10', ring: 'ring-orange-300', dot: 'bg-orange-500', emoji: '🟠' },
  medium:  { label: 'MEDIUM',  color: 'bg-amber-500',   text: 'text-amber-700 dark:text-amber-300',   bg: 'bg-amber-50 dark:bg-amber-500/10',   ring: 'ring-amber-300', dot: 'bg-amber-500', emoji: '🟡' },
  routine: { label: 'ROUTINE', color: 'bg-emerald-500', text: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-500/10', ring: 'ring-emerald-300', dot: 'bg-emerald-500', emoji: '🟢' },
};

const ACTION_LABELS = {
  whatsapp_polite: 'WhatsApp (polite)',
  whatsapp_firm:   'WhatsApp (firm)',
  call:            'Call directly',
  escalate:        'Escalate to manager',
  wait:            'Wait — PTP active',
  visit:           'Visit in person',
};

const AIWorklist = ({ onAction }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [data, setData] = useState({ items: [], generated_at: null, model: '' });
  const [filter, setFilter] = useState('todo'); // todo / all / done
  const [search, setSearch] = useState('');

  const fetchWorklist = useCallback(() => {
    setLoading(true);
    arApi.worklistToday()
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load worklist'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(fetchWorklist, [fetchWorklist]);

  // Poll while generation is in progress
  useEffect(() => {
    if (data?.status !== 'generating') return;
    let polls = 0;
    const interval = setInterval(async () => {
      polls += 1;
      try {
        const r = await arApi.worklistToday();
        setData(r.data);
        if (r.data?.status === 'ready') {
          setGenerating(false);
          toast.success(`Worklist ready · ${(r.data.items || []).length} items`);
        } else if (r.data?.status === 'failed') {
          setGenerating(false);
          toast.error(r.data.error || 'Generation failed — try again');
        }
      } catch { /* ignore single-tick failures, keep polling */ }
      // Client-side stuck-poll safeguard: if 25 polls (~100s) elapse without ready/failed,
      // surface a recovery toast and let user retry. Backend's 5-min stale guard will catch the rest.
      if (polls >= 25 && data?.status === 'generating') {
        setGenerating(false);
        toast.error('Generation is taking too long. The system will auto-recover in ~5 minutes — retry then.');
        clearInterval(interval);
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [data?.status]);

  const generate = async () => {
    setGenerating(true);
    toast.message('Asking Claude to analyse your debtors… ~30s');
    try {
      const r = await arApi.worklistGenerate();
      // Server returns 202 Accepted immediately; status=generating in response
      if (r.data?.status === 'generating') {
        // Trigger polling by reflecting status in local state
        setData(d => ({ ...d, status: 'generating', started_at: r.data.started_at }));
      } else if (r.data?.items) {
        // Backward compat (in case server runs synchronously)
        setData(r.data);
        setGenerating(false);
        toast.success(`Generated ${r.data?.count || 0} action items`);
      }
    } catch (e) {
      setGenerating(false);
      toast.error(e?.response?.data?.detail || 'Failed to start generation');
    }
  };

  const updateItem = async (item, action, note = '') => {
    try {
      await arApi.worklistUpdateItem(item.id, action, note);
      setData(d => ({
        ...d,
        items: d.items.map(it => it.id === item.id ? { ...it, outcome: action === 'reset' ? null : action } : it),
      }));
      toast.success(action === 'reset' ? 'Reopened' : `Marked ${action}`);
    } catch { toast.error('Failed to update'); }
  };

  const filtered = useMemo(() => {
    let items = data.items || [];
    if (filter === 'todo') items = items.filter(it => !it.outcome);
    if (filter === 'done') items = items.filter(it => it.outcome);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(it =>
        (it.customer_name || '').toLowerCase().includes(q) ||
        (it.invoice_number || '').toLowerCase().includes(q) ||
        (it.co_number || '').toLowerCase().includes(q)
      );
    }
    return items;
  }, [data.items, filter, search]);

  const counts = useMemo(() => {
    const it = data.items || [];
    return {
      total: it.length,
      todo: it.filter(x => !x.outcome).length,
      urgent: it.filter(x => x.priority === 'urgent' && !x.outcome).length,
      high: it.filter(x => x.priority === 'high' && !x.outcome).length,
      done: it.filter(x => x.outcome).length,
      paid_today: it.filter(x => x.outcome === 'paid').length,
      promised_today: it.filter(x => x.outcome === 'promised').length,
    };
  }, [data.items]);

  return (
    <div className="space-y-4">
      {/* Hero */}
      <Card className="overflow-hidden bg-gradient-to-br from-violet-500 via-indigo-500 to-blue-500 border-0">
        <CardContent className="p-4 md:p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-4 w-4" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold opacity-90">AI Daily Worklist</span>
              </div>
              <h2 className="font-heading text-xl md:text-3xl font-bold leading-tight">
                {data?.status === 'generating' ? 'Analysing your debtors…'
                  : data?.status === 'failed' ? 'Generation failed'
                  : counts.todo > 0 ? `${counts.todo} action${counts.todo === 1 ? '' : 's'} for today`
                  : data.items?.length ? 'All caught up. 🎉' : 'No worklist yet'}
              </h2>
              <p className="text-xs md:text-sm opacity-90 mt-1">
                {data?.status === 'generating'
                  ? `Claude is reading ${(data.items?.length || 'all open')} invoices — usually takes 30-45 seconds. You can stay on this page or come back later.`
                  : data?.status === 'failed' && data?.error
                  ? `Last run errored: ${data.error.slice(0, 140)}. Click Refresh worklist to retry.`
                  : data.generated_at
                  ? `Last refreshed ${new Date(data.generated_at).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })} · Claude Sonnet 4.5`
                  : 'Click Generate to let Claude analyse your debtors and create a prioritised action list.'}
              </p>
            </div>
            <Button
              onClick={generate}
              disabled={generating || data?.status === 'generating'}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white font-semibold shadow-lg shadow-black/20 w-full md:w-auto flex-shrink-0"
              data-testid="ar-worklist-generate"
            >
              {generating || data?.status === 'generating' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Zap className="h-4 w-4 mr-1.5" />}
              {data?.status === 'generating' ? 'Generating…' : data.items?.length ? 'Refresh worklist' : 'Generate worklist'}
            </Button>
          </div>
          {data.items?.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2 md:gap-3 mt-4 md:mt-5">
              {[
                { label: 'To do', value: counts.todo, color: 'bg-white/15' },
                { label: 'Urgent', value: counts.urgent, color: 'bg-red-500/30' },
                { label: 'High', value: counts.high, color: 'bg-orange-500/30' },
                { label: 'Done', value: counts.done, color: 'bg-emerald-500/30' },
                { label: 'Promised', value: counts.promised_today, color: 'bg-blue-500/30' },
              ].map(s => (
                <div key={s.label} className={cn("rounded-lg px-2.5 md:px-3 py-1.5 md:py-2 backdrop-blur-sm border border-white/20", s.color)}>
                  <p className="text-[10px] uppercase tracking-wider opacity-90 font-semibold">{s.label}</p>
                  <p className="text-xl md:text-2xl font-bold tabular-nums">{s.value}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[140px]" data-testid="ar-worklist-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todo">To do ({counts.todo})</SelectItem>
            <SelectItem value="done">Done ({counts.done})</SelectItem>
            <SelectItem value="all">All ({counts.total})</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search customer / invoice / CO"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="ar-worklist-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={fetchWorklist} disabled={loading} data-testid="ar-worklist-refresh">
          <RefreshCw className={cn("h-4 w-4 mr-1", loading && "animate-spin")} /> Refresh
        </Button>
      </div>

      {/* Items */}
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-32 w-full" />)}</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          {!data.items?.length ? (
            <>
              <Sparkles className="h-12 w-12 mx-auto text-violet-400 mb-3" />
              <p className="text-sm text-muted-foreground">Click <strong>Generate worklist</strong> above to let Claude analyse your debtors.</p>
              <p className="text-xs text-muted-foreground mt-1">Takes ~30 seconds, costs ~1¢.</p>
            </>
          ) : filter === 'todo' ? (
            <>
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
              <p className="text-sm text-muted-foreground">All worklist items handled. 🎉 Generate a fresh list tomorrow.</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No items match your filters.</p>
          )}
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => <WorklistItem key={item.id} item={item} onAction={onAction} onUpdate={updateItem} navigate={navigate} />)}
        </div>
      )}
    </div>
  );
};

const WorklistItem = ({ item, onAction, onUpdate, navigate }) => {
  const meta = PRIORITY_META[item.priority] || PRIORITY_META.routine;
  const [showDraft, setShowDraft] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showNoteInput, setShowNoteInput] = useState(false);

  const sendDraftWhatsApp = () => {
    // Open Reminder dialog with the AI draft pre-filled
    onAction('reminder', {
      order_id: item.order_id,
      dispatch_id: item.dispatch_id,
      invoice_number: item.invoice_number,
      customer_name: item.customer_name,
      customer_phone: item.customer_phone,
      balance: item.balance,
      total_amount: item.total_amount,
      notification_phones: [],
      _ai_draft: item.draft_message,
    });
  };

  return (
    <Card className={cn(
      "transition-all overflow-hidden",
      item.outcome ? "opacity-60" : "hover:shadow-md",
      !item.outcome && `border-l-4`,
    )} style={!item.outcome ? { borderLeftColor: `var(--${item.priority}-color, currentColor)` } : {}}
      data-testid={`worklist-item-${item.invoice_number}`}
    >
      <CardContent className="p-0">
        <div className="px-4 py-3 flex items-start gap-3">
          <div className={cn("h-2.5 w-2.5 rounded-full flex-shrink-0 mt-2", meta.dot)} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={cn("text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded", meta.bg, meta.text)}>
                  {meta.emoji} {meta.label}
                </span>
                <button onClick={() => navigate(`/orders/${item.order_id}`)} className="text-sm font-bold hover:underline flex items-center gap-1" data-testid={`worklist-customer-${item.invoice_number}`}>
                  {item.customer_name}
                  <ExternalLink className="h-3 w-3 opacity-50" />
                </button>
                {item.branch && <Badge variant="outline" className="text-[10px]">{item.branch}</Badge>}
                {item.trust_grade && <Badge variant="outline" className="text-[10px]">Trust {item.trust_grade}</Badge>}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-lg font-bold tabular-nums">{formatINR(item.balance)}</p>
                {item.days_overdue > 0 && <p className="text-[11px] text-rose-600">{item.days_overdue}d overdue</p>}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Inv {item.invoice_number} · CO {item.co_number}
              {item.salesperson && <> · {item.salesperson}</>}
              {item.reminder_count > 0 && <> · <span className="text-amber-600">{item.reminder_count} reminders sent</span></>}
              {item.active_ptp && <> · <span className="text-blue-600">PTP {item.active_ptp.promised_date}</span></>}
            </p>
            {/* AI Context */}
            <div className={cn("mt-2 p-2.5 rounded-lg text-xs", meta.bg)}>
              <p className="flex items-start gap-1.5">
                <Sparkles className="h-3 w-3 mt-0.5 flex-shrink-0 opacity-70" />
                <span className={meta.text}>
                  <strong>AI says:</strong> {item.context}
                  <span className="ml-1.5 font-semibold">→ {ACTION_LABELS[item.action] || item.action}</span>
                </span>
              </p>
              {showDraft && (
                <div className="mt-2 pt-2 border-t border-current/10">
                  <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70 mb-1">Suggested message</p>
                  <p className="whitespace-pre-wrap text-foreground/90">{item.draft_message}</p>
                </div>
              )}
            </div>
            {/* Outcome badge */}
            {item.outcome && (
              <div className="mt-2 flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] capitalize bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                  ✓ {item.outcome}
                </Badge>
                <button onClick={() => onUpdate(item, 'reset')} className="text-[10px] text-muted-foreground hover:text-foreground underline">
                  Reset
                </button>
              </div>
            )}
            {/* Actions */}
            {!item.outcome && (
              <div className="mt-3 space-y-1.5">
                {/* Primary actions — always visible */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white flex-1 sm:flex-none" onClick={sendDraftWhatsApp} data-testid={`worklist-whatsapp-${item.invoice_number}`}>
                    <Send className="h-3 w-3 mr-1" /> WhatsApp
                  </Button>
                  {item.customer_phone && (
                    <a href={`tel:${item.customer_phone}`} className="h-8 px-3 text-xs rounded-md border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-700 inline-flex items-center justify-center flex-1 sm:flex-none" data-testid={`worklist-call-${item.invoice_number}`}>
                      <Phone className="h-3 w-3 mr-1" /> Call
                    </a>
                  )}
                  <Button size="sm" variant="outline" className="h-8 text-xs hidden sm:inline-flex" onClick={() => setShowDraft(s => !s)} data-testid={`worklist-toggle-draft-${item.invoice_number}`}>
                    <Edit3 className="h-3 w-3 mr-1" /> {showDraft ? 'Hide' : 'View'} draft
                  </Button>
                </div>
                {/* Outcome buttons — secondary */}
                <div className="flex items-center gap-1 flex-wrap text-xs border-t pt-1.5">
                  <Button size="sm" variant="outline" className="h-7 text-[11px] sm:hidden" onClick={() => setShowDraft(s => !s)} data-testid={`worklist-toggle-draft-mobile-${item.invoice_number}`}>
                    <Edit3 className="h-3 w-3 mr-1" /> {showDraft ? 'Hide' : 'View'} draft
                  </Button>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground hidden md:inline-block">Mark:</span>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-emerald-600" title="Mark paid" onClick={() => onUpdate(item, 'paid')} data-testid={`worklist-paid-${item.invoice_number}`}>
                    Paid
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-blue-600" title="Promised" onClick={() => onUpdate(item, 'promised')}>
                    Promised
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-muted-foreground" title="No reply" onClick={() => onUpdate(item, 'no_reply')}>
                    No reply
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" title="Snooze 24h" onClick={() => onUpdate(item, 'snooze')} data-testid={`worklist-snooze-${item.invoice_number}`}>
                    <BellOff className="h-3 w-3 mr-1" /> <span className="hidden sm:inline">Snooze</span>
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" title="Mark done" onClick={() => onUpdate(item, 'done')} data-testid={`worklist-done-${item.invoice_number}`}>
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 sm:mr-1" /> <span className="hidden sm:inline">Done</span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Main page ───────────────────────────────────────────────
const Receivables = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('worklist');
  const [actionInvoice, setActionInvoice] = useState(null);
  const [action, setAction] = useState(null); // 'reminder' | 'payment' | 'detail'
  const [refreshKey, setRefreshKey] = useState(0);

  const canAccess = ['admin', 'super_admin', 'finance'].includes(user?.role);

  useEffect(() => {
    if (!canAccess) {
      toast.error("You don't have access to Receivables");
      navigate('/');
    }
  }, [canAccess, navigate]);

  const onAction = (a, inv) => { setAction(a); setActionInvoice(inv); };
  const closeAction = () => { setAction(null); setActionInvoice(null); };
  const onSaved = () => { setRefreshKey(k => k + 1); };

  if (!canAccess) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-neutral-950" data-testid="receivables-page">
      <Header title="Receivables" />
      <div className="px-3 md:px-8 py-3 md:py-5 max-w-screen-2xl mx-auto">
        <Tabs value={tab} onValueChange={setTab}>
          <div className="-mx-3 md:mx-0 mb-3 md:mb-4 px-3 md:px-0 overflow-x-auto scrollbar-hide">
            <TabsList className="inline-flex w-max md:w-auto md:flex-wrap">
              <TabsTrigger value="worklist" data-testid="ar-tab-worklist" className="bg-gradient-to-r from-violet-500/10 to-indigo-500/10 data-[state=active]:from-violet-500 data-[state=active]:to-indigo-500 data-[state=active]:text-white whitespace-nowrap">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> AI Worklist
              </TabsTrigger>
              <TabsTrigger value="hub" data-testid="ar-tab-hub" className="whitespace-nowrap"><Wallet className="h-3.5 w-3.5 mr-1.5" /> AR Hub</TabsTrigger>
              <TabsTrigger value="invoices" data-testid="ar-tab-invoices" className="whitespace-nowrap"><Receipt className="h-3.5 w-3.5 mr-1.5" /> Invoices</TabsTrigger>
              <TabsTrigger value="aging" data-testid="ar-tab-aging" className="whitespace-nowrap"><Calendar className="h-3.5 w-3.5 mr-1.5" /> Aging</TabsTrigger>
              <TabsTrigger value="forecast" data-testid="ar-tab-forecast" className="whitespace-nowrap"><TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Forecast</TabsTrigger>
              <TabsTrigger value="ptp" data-testid="ar-tab-ptp" className="whitespace-nowrap"><Clock className="h-3.5 w-3.5 mr-1.5" /> Promises</TabsTrigger>
              <TabsTrigger value="settings" data-testid="ar-tab-settings" className="whitespace-nowrap"><SettingsIcon className="h-3.5 w-3.5 mr-1.5" /> Settings</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="worklist" key={`wl-${refreshKey}`}><AIWorklist onAction={onAction} /></TabsContent>
          <TabsContent value="hub" key={`hub-${refreshKey}`}><ARHub onOpenInvoice={(i) => onAction('detail', i)} /></TabsContent>
          <TabsContent value="invoices" key={`inv-${refreshKey}`}><OpenInvoices onAction={onAction} /></TabsContent>
          <TabsContent value="aging" key={`ag-${refreshKey}`}><AgingReport /></TabsContent>
          <TabsContent value="forecast" key={`fc-${refreshKey}`}><CashForecast /></TabsContent>
          <TabsContent value="ptp" key={`ptp-${refreshKey}`}><DisputesAndPTP /></TabsContent>
          <TabsContent value="settings"><ARSettingsPanel /></TabsContent>
        </Tabs>
      </div>

      <ReminderDialog open={action === 'reminder'} invoice={actionInvoice} onClose={closeAction} onSaved={onSaved} />
      <DetailDialog open={action === 'detail'} invoice={actionInvoice} onClose={closeAction} onSaved={onSaved} />
    </div>
  );
};

export default Receivables;
